import React, { useState, useEffect } from 'react';
import { millShippingAPI, bolAPI, palletLabelAPI } from '../../services/millAPI';
import MobileScrollTable from '../../components/common/MobileScrollTable';
import './Mill.css';

interface PalletContent {
  feed_name: string;
  quantity: number;
  weight: number;
  // Batch/lot numbers from the persisted PalletFill recall snapshots.
  lot_numbers?: string[];
}

interface Pallet {
  contents: PalletContent[];
  total_bags: number;
  total_weight: number;
}

interface OrderItem {
  id: number;
  formula_name: string;
  quantity_lbs: number;
  package_type: string;
  package_type_display: string;
  number_of_packages: number;
  is_pelleted: boolean;
  fulfillment_count: number;
}

interface FulfillmentStatus {
  ready_pct: number;
  packaging_pct: number;
  milling_pct: number;
  store_task_pct: number;
  ready_lbs: number;
  packaging_lbs: number;
  milling_lbs: number;
  store_task_lbs: number;
}

interface PipelineSummary {
  total_orders: number;
  ready_to_ship: number;
  packaging: number;
  milling: number;
  waiting_store: number;
}

interface ShippingOrder {
  id: number;
  order_number: string;
  customer_name: string;
  customer_phone: string;
  delivery_address: string;
  delivery_method: string;
  delivery_method_display: string;
  delivery_date: string;
  status: string;
  status_display: string;
  total_weight: number;
  items: OrderItem[];
  notes: string;
  special_instructions: string;
  fulfillment_status: FulfillmentStatus;
  shipping_quote_number?: string;
}

const MillShipping: React.FC = () => {
  const [orders, setOrders] = useState<ShippingOrder[]>([]);
  const [pipelineSummary, setPipelineSummary] = useState<PipelineSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Ship modal
  const [showShipModal, setShowShipModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ShippingOrder | null>(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [proNumber, setProNumber] = useState('');
  const [shipDate, setShipDate] = useState('');
  const [carrier, setCarrier] = useState('');
  const [shippingNotes, setShippingNotes] = useState('');
  const [loadingBOLData, setLoadingBOLData] = useState(false);
  const [bolDataLoaded, setBolDataLoaded] = useState(false);

  // BOL modal
  const [showBOLModal, setShowBOLModal] = useState(false);
  const [bolOrder, setBolOrder] = useState<ShippingOrder | null>(null);
  const [bolCarrier, setBolCarrier] = useState('OAK_HARBOR');
  const [bolShipDate, setBolShipDate] = useState('');
  const [bolDriverName, setBolDriverName] = useState('');
  const [bolTrailerNumber, setBolTrailerNumber] = useState('');
  const [bolSpecialInstructions, setBolSpecialInstructions] = useState('');
  const [bolTrackingNumber, setBolTrackingNumber] = useState('');
  const [bolQuoteNumber, setBolQuoteNumber] = useState('');
  const [generatingBOL, setGeneratingBOL] = useState(false);

  // BOL replace confirmation modal
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [existingBOL, setExistingBOL] = useState<{ id: number; bol_number: string; status_display: string } | null>(null);

  // Existing BOL for the order in the BOL modal (two-door: the office may
  // have already created it from the invoice screen — pull, don't fork).
  const [existingOrderBol, setExistingOrderBol] = useState<{
    id: number; bol_number: string; status: string; status_display?: string;
    created_by?: string | null; created_at?: string; is_stale?: boolean;
    ship_date?: string;
  } | null>(null);
  const [bolActionWorking, setBolActionWorking] = useState(false);

  // Pallet Label modal
  const [showPalletModal, setShowPalletModal] = useState(false);
  const [palletOrder, setPalletOrder] = useState<ShippingOrder | null>(null);
  const [pallets, setPallets] = useState<Pallet[]>([]);
  const [palletShipDate, setPalletShipDate] = useState('');
  const [palletError, setPalletError] = useState<string | null>(null);
  const [loadingPallets, setLoadingPallets] = useState(false);
  const [generatingLabels, setGeneratingLabels] = useState(false);
  const [labelPreviews, setLabelPreviews] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await millShippingAPI.getList();
      setOrders(response.data.results || []);
      setPipelineSummary(response.data.pipeline_summary || null);
    } catch (err: any) {
      console.error('Error loading shipping orders:', err);
      setError('Failed to load shipping data');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleOpenShipModal = async (order: ShippingOrder) => {
    setSelectedOrder(order);
    setTrackingNumber('');
    setProNumber('');
    setShippingNotes('');
    setBolDataLoaded(false);
    setShipDate(new Date().toISOString().split('T')[0]);

    // Check if this is a Will Call order
    const isWillCall = order.delivery_method?.toUpperCase().includes('WILL_CALL') ||
                       order.delivery_method_display?.toLowerCase().includes('will call');

    if (isWillCall) {
      setCarrier('Customer Pickup');
    } else {
      setCarrier('');
    }

    setShowShipModal(true);

    // Try to fetch BOL data to auto-populate the form
    try {
      setLoadingBOLData(true);
      const response = await bolAPI.getForOrder(order.id);
      const bol = response.data.bol;

      if (bol) {
        // Auto-populate from BOL data
        // BOL number IS the PRO number for Fastway shipments
        setProNumber(bol.bol_number || '');
        setCarrier('Fastway Freight');
        setShippingNotes(bol.special_instructions || '');
        if (bol.ship_date) {
          setShipDate(bol.ship_date);
        }
        setBolDataLoaded(true);
      }
    } catch (err) {
      // Silently fail - BOL data is optional
      console.log('No BOL found for order, fields will be empty');
    } finally {
      setLoadingBOLData(false);
    }
  };

  const handleOpenBOLModal = async (order: ShippingOrder) => {
    setBolOrder(order);
    setBolCarrier('FASTWAY');
    setBolShipDate(new Date().toISOString().split('T')[0]);
    setBolDriverName('');
    setBolTrailerNumber('');
    setBolSpecialInstructions(order.special_instructions || '');
    setBolTrackingNumber('');
    // Pre-fill from the invoice's freight quote # (blank before this made
    // it easy to ship without the quote, or to silently diverge from the
    // invoice). Mill user can still override for dock re-quotes.
    setBolQuoteNumber(order.shipping_quote_number || '');
    setExistingOrderBol(null);
    setShowBOLModal(true);

    // Two-door pull: the office may already have created this order's
    // BOL from the invoice screen — surface it so the crew prints/
    // regenerates the ONE record instead of blank-creating a new one.
    try {
      const response = await bolAPI.getForOrder(order.id);
      if (response.data.bol) setExistingOrderBol(response.data.bol);
    } catch {
      // No existing BOL — fresh-create flow as always.
    }
  };

  const downloadBolPdf = async (bolId: number, bolNumber: string) => {
    await bolAPI.generatePDF(bolId);
    const pdfResponse = await bolAPI.downloadPDF(bolId);
    const url = window.URL.createObjectURL(new Blob([pdfResponse.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${bolNumber}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handlePrintExistingBOL = async () => {
    if (!existingOrderBol) return;
    try {
      setBolActionWorking(true);
      await downloadBolPdf(existingOrderBol.id, existingOrderBol.bol_number);
      showSuccess(`BOL ${existingOrderBol.bol_number} downloaded`);
    } catch (err: any) {
      alert('Failed to print BOL: ' + (err.response?.data?.error || err.message));
    } finally {
      setBolActionWorking(false);
    }
  };

  const handleRegenerateExistingBOL = async () => {
    if (!bolOrder || !existingOrderBol) return;
    try {
      setBolActionWorking(true);
      await bolAPI.regenerateForOrder(bolOrder.id);
      const response = await bolAPI.getForOrder(bolOrder.id);
      if (response.data.bol) setExistingOrderBol(response.data.bol);
      showSuccess(`BOL ${existingOrderBol.bol_number} regenerated from current pallets`);
    } catch (err: any) {
      alert('Failed to regenerate BOL: ' + (err.response?.data?.error || err.message));
    } finally {
      setBolActionWorking(false);
    }
  };

  const handleGenerateBOL = async (replaceExisting: boolean = false) => {
    if (!bolOrder) return;

    try {
      setGeneratingBOL(true);

      // Create the BOL
      const createResponse = await bolAPI.create({
        order_ids: [bolOrder.id],
        ship_date: bolShipDate,
        carrier: bolCarrier,
        driver_name: bolDriverName || undefined,
        trailer_number: bolTrailerNumber || undefined,
        special_instructions: bolSpecialInstructions || undefined,
        bol_number: bolTrackingNumber,
        quote_number: bolQuoteNumber || undefined,
        replace_existing: replaceExisting,
      });

      // Check if response indicates existing BOL (returned as 200 OK with flag)
      if (createResponse.data.existing_bol) {
        // Show confirmation dialog instead of error
        setExistingBOL({
          id: createResponse.data.bol_id,
          bol_number: createResponse.data.bol_number,
          status_display: createResponse.data.status_display,
        });
        setShowReplaceModal(true);
        setGeneratingBOL(false);
        return;
      }

      const bolId = createResponse.data.bol_id;

      // Generate the PDF
      await bolAPI.generatePDF(bolId);

      // Download the PDF
      const pdfResponse = await bolAPI.downloadPDF(bolId);

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([pdfResponse.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${createResponse.data.bol_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess(`BOL ${createResponse.data.bol_number} generated and downloaded`);
      setShowBOLModal(false);
      setShowReplaceModal(false);
      setBolOrder(null);
      setExistingBOL(null);
    } catch (err: any) {
      console.error('Error generating BOL:', err);
      alert('Failed to generate BOL: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingBOL(false);
    }
  };

  const handleOpenPalletModal = async (order: ShippingOrder) => {
    setPalletOrder(order);
    setPalletShipDate(new Date().toISOString().split('T')[0]);
    setPallets([]);
    setLabelPreviews([]);
    setShowPreview(false);
    setShowPalletModal(true);

    // Load the persisted packed pallets (the same rows the BOL reads)
    await loadPackedPallets(order.id);
  };

  const loadPackedPallets = async (orderId: number) => {
    try {
      setLoadingPallets(true);
      setPalletError(null);
      const response = await palletLabelAPI.autoDistribute(orderId);

      const palletData = (response.data.pallets || []).map((p: any) => {
        const contents = p.contents || [];
        return {
          contents,
          total_bags: p.total_bags ?? contents.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0),
          total_weight: p.total_weight ?? contents.reduce((sum: number, item: any) => sum + (item.weight || 0), 0),
        };
      });

      setPallets(palletData);
    } catch (err: any) {
      console.error('Error loading packed pallets:', err);
      setPallets([]);
      setPalletError(err.response?.data?.error || err.message || 'Failed to load packed pallets');
    } finally {
      setLoadingPallets(false);
    }
  };

  const handleRefreshPallets = async () => {
    if (palletOrder) {
      setShowPreview(false);
      setLabelPreviews([]);
      await loadPackedPallets(palletOrder.id);
    }
  };

  const handlePreviewLabels = async () => {
    if (!palletOrder || pallets.length === 0) return;

    try {
      setGeneratingLabels(true);
      const response = await palletLabelAPI.preview({
        order_id: palletOrder.id,
        ship_date: palletShipDate,
      });

      setLabelPreviews((response.data.labels || []).map((l: any) => l.image));
      setShowPreview(true);
    } catch (err: any) {
      console.error('Error previewing labels:', err);
      alert('Failed to preview labels: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingLabels(false);
    }
  };

  const handlePrintPalletLabels = async () => {
    if (!palletOrder || pallets.length === 0) return;

    try {
      setGeneratingLabels(true);

      // First generate and save the labels (built server-side from the
      // persisted packed pallets — the same rows the BOL reads)
      await palletLabelAPI.generate({
        order_id: palletOrder.id,
      });

      // Then print them
      const printResponse = await palletLabelAPI.printLabels(palletOrder.id);

      // Open print HTML in new window
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printResponse.data.print_html);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      }

      showSuccess(`${pallets.length} pallet label(s) sent to printer`);
      setShowPalletModal(false);
      setPalletOrder(null);
    } catch (err: any) {
      console.error('Error printing pallet labels:', err);
      alert('Failed to print labels: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingLabels(false);
    }
  };

  // B5: pallet composition is read-only here — it mirrors the persisted
  // packed pallets (the same rows the BOL reads), so labels can never
  // contradict the BOL. Editing happens in Packaging, not on the label.

  const handleMarkShipped = async () => {
    if (!selectedOrder) return;

    try {
      setProcessing(selectedOrder.id);
      await millShippingAPI.markShipped(selectedOrder.id, {
        pro_number: proNumber,
        tracking_number: trackingNumber || proNumber,
        carrier,
        ship_date: shipDate,
        notes: shippingNotes,
      });

      // Remove from list
      setOrders(prev => prev.filter(o => o.id !== selectedOrder.id));

      showSuccess(`Order #${selectedOrder.order_number} marked as shipped`);
      setShowShipModal(false);
      setSelectedOrder(null);
    } catch (err: any) {
      console.error('Error marking shipped:', err);
      alert('Failed to mark as shipped: ' + (err.response?.data?.error || err.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleMarkReadyToShip = async (orderId: number) => {
    try {
      setProcessing(orderId);
      await millShippingAPI.markReadyToShip(orderId);

      // Update local state
      setOrders(prev => prev.map(order =>
        order.id === orderId
          ? { ...order, status: 'READY_TO_SHIP', status_display: 'Ready to Ship' }
          : order
      ));

      showSuccess('Order marked as ready to ship');
    } catch (err: any) {
      console.error('Error marking ready to ship:', err);
      alert('Failed to update: ' + (err.response?.data?.error || err.message));
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const isToday = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPastDue = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getDeliveryMethodIcon = (method: string) => {
    switch (method?.toUpperCase()) {
      case 'PICKUP':
        return '🏪';
      case 'DELIVERY':
        return '🚚';
      case 'FREIGHT':
        return '🚛';
      default:
        return '📦';
    }
  };

  if (loading) {
    return (
      <div className="mill-page">
        <div className="mill-loading">
          <div className="mill-loading-spinner"></div>
          <p>Loading shipping orders...</p>
        </div>
      </div>
    );
  }

  // Group orders by delivery date
  const ordersByDate: { [key: string]: ShippingOrder[] } = {};
  orders.forEach(order => {
    const dateKey = order.delivery_date;
    if (!ordersByDate[dateKey]) {
      ordersByDate[dateKey] = [];
    }
    ordersByDate[dateKey].push(order);
  });

  // Sort dates: past due first (chronological), then today, then future
  const todayStr = new Date().toISOString().split('T')[0];
  const sortedDates = Object.keys(ordersByDate).sort((a, b) => {
    const aPast = a < todayStr;
    const bPast = b < todayStr;
    // Past due dates come first
    if (aPast && !bPast) return -1;
    if (!aPast && bPast) return 1;
    return a.localeCompare(b);
  });

  const pastDueCount = sortedDates.filter(d => d < todayStr).reduce(
    (sum, d) => sum + ordersByDate[d].length, 0
  );

  return (
    <div className="mill-page">
      {/* Success Toast */}
      {successMessage && (
        <div className="mill-toast mill-toast-success">
          <span className="toast-icon">✓</span>
          {successMessage}
        </div>
      )}

      <div className="mill-page-header">
        <h1>Shipping Station</h1>
        <p className="mill-page-subtitle">Ship packaged orders to customers</p>
      </div>

      {/* Pipeline Summary */}
      {pipelineSummary && pipelineSummary.total_orders > 0 && (
        <div className="shipping-pipeline-summary">
          <div className="pipeline-stat">
            <span className="pipeline-value">{pipelineSummary.total_orders}</span>
            <span className="pipeline-label">Total Orders</span>
          </div>
          <div className="pipeline-stat ready">
            <span className="pipeline-value">{pipelineSummary.ready_to_ship}</span>
            <span className="pipeline-label">Ready to Ship</span>
          </div>
          <div className="pipeline-stat packaging">
            <span className="pipeline-value">{pipelineSummary.packaging}</span>
            <span className="pipeline-label">Packaging</span>
          </div>
          {pipelineSummary.milling > 0 && (
            <div className="pipeline-stat milling">
              <span className="pipeline-value">{pipelineSummary.milling}</span>
              <span className="pipeline-label">In Milling</span>
            </div>
          )}
          {pipelineSummary.waiting_store > 0 && (
            <div className="pipeline-stat store">
              <span className="pipeline-value">{pipelineSummary.waiting_store}</span>
              <span className="pipeline-label">Waiting Store</span>
            </div>
          )}
          {pastDueCount > 0 && (
            <div className="pipeline-stat past-due">
              <span className="pipeline-value">{pastDueCount}</span>
              <span className="pipeline-label">Past Due</span>
            </div>
          )}
        </div>
      )}

      {error && <div className="mill-alert mill-alert-error">{error}</div>}

      {orders.length === 0 ? (
        <div className="mill-empty-state">
          <div className="empty-icon">🚚</div>
          <h3>No Orders Ready to Ship</h3>
          <p>Package orders to see them here.</p>
        </div>
      ) : (
        <div className="mill-shipping-list">
          {sortedDates.map(dateKey => {
            const dateOrders = ordersByDate[dateKey];
            const pastDue = isPastDue(dateKey);
            const today = isToday(dateKey);

            return (
              <div key={dateKey} className="shipping-date-group">
                <h2 className={`date-header ${pastDue ? 'past-due' : ''} ${today ? 'today' : ''}`}>
                  {today && <span className="today-badge">TODAY</span>}
                  {pastDue && <span className="past-due-badge">PAST DUE</span>}
                  {formatDate(dateKey)}
                  <span className="order-count">{dateOrders.length} order{dateOrders.length !== 1 ? 's' : ''}</span>
                </h2>

                <div className="shipping-orders-grid">
                  {dateOrders.map(order => (
                    <div
                      key={order.id}
                      className={`shipping-order-card ${order.status === 'READY_TO_SHIP' ? 'ready' : 'packaging'}`}
                    >
                      <div className="order-header">
                        <div className="order-number-section">
                          <span className="order-number">#{order.order_number}</span>
                          <span className={`status-pill ${order.status.toLowerCase().replace('_', '-')}`}>
                            {order.status_display}
                          </span>
                        </div>
                        <span className="delivery-method">
                          {getDeliveryMethodIcon(order.delivery_method)} {order.delivery_method_display}
                        </span>
                      </div>

                      {/* Fulfillment Progress Bar */}
                      {order.fulfillment_status && (
                        <div className="fulfillment-progress">
                          <div className="fulfillment-bar">
                            {order.fulfillment_status.ready_pct > 0 && (
                              <div
                                className="fulfillment-segment ready"
                                style={{ width: `${order.fulfillment_status.ready_pct}%` }}
                                title={`Ready: ${order.fulfillment_status.ready_lbs.toLocaleString()} lbs (${order.fulfillment_status.ready_pct}%)`}
                              />
                            )}
                            {order.fulfillment_status.packaging_pct > 0 && (
                              <div
                                className="fulfillment-segment packaging"
                                style={{ width: `${order.fulfillment_status.packaging_pct}%` }}
                                title={`Packaging: ${order.fulfillment_status.packaging_lbs.toLocaleString()} lbs (${order.fulfillment_status.packaging_pct}%)`}
                              />
                            )}
                            {order.fulfillment_status.milling_pct > 0 && (
                              <div
                                className="fulfillment-segment milling"
                                style={{ width: `${order.fulfillment_status.milling_pct}%` }}
                                title={`Milling: ${order.fulfillment_status.milling_lbs.toLocaleString()} lbs (${order.fulfillment_status.milling_pct}%)`}
                              />
                            )}
                            {order.fulfillment_status.store_task_pct > 0 && (
                              <div
                                className="fulfillment-segment store"
                                style={{ width: `${order.fulfillment_status.store_task_pct}%` }}
                                title={`Store Task: ${order.fulfillment_status.store_task_lbs.toLocaleString()} lbs (${order.fulfillment_status.store_task_pct}%)`}
                              />
                            )}
                          </div>
                          {order.fulfillment_status.ready_pct < 100 && (
                            <div className="fulfillment-legend">
                              {order.fulfillment_status.ready_pct > 0 && <span className="legend-item ready">Ready {order.fulfillment_status.ready_pct}%</span>}
                              {order.fulfillment_status.packaging_pct > 0 && <span className="legend-item packaging">Packaging {order.fulfillment_status.packaging_pct}%</span>}
                              {order.fulfillment_status.milling_pct > 0 && <span className="legend-item milling">Milling {order.fulfillment_status.milling_pct}%</span>}
                              {order.fulfillment_status.store_task_pct > 0 && <span className="legend-item store">Store {order.fulfillment_status.store_task_pct}%</span>}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="customer-info">
                        <div className="customer-name">{order.customer_name}</div>
                        {order.customer_phone && (
                          <div className="customer-phone">📞 {order.customer_phone}</div>
                        )}
                        {order.delivery_address && (
                          <div className="delivery-address">📍 {order.delivery_address}</div>
                        )}
                      </div>

                      <div className="order-items">
                        {order.items.map(item => (
                          <div key={item.id} className="order-item">
                            <span className="item-formula">{item.formula_name || 'Unnamed item'}</span>
                            <span className="item-quantity">
                              {item.quantity_lbs.toLocaleString()} lbs
                              {item.is_pelleted && <span className="pellet-badge">Pellet</span>}
                            </span>
                            <span className="item-package">
                              {item.number_of_packages > 0
                                ? `${item.number_of_packages} ${item.package_type_display}`
                                : 'Bulk'}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="order-total">
                        Total: <strong>{order.total_weight.toLocaleString()} lbs</strong>
                      </div>

                      {order.notes && (
                        <div className="order-notes">
                          <strong>Notes:</strong> {order.notes}
                        </div>
                      )}

                      <div className="order-actions">
                        <button
                          className="mill-btn mill-btn-outline"
                          onClick={() => handleOpenBOLModal(order)}
                        >
                          Generate BOL
                        </button>
                        <button
                          className="mill-btn mill-btn-outline"
                          onClick={() => handleOpenPalletModal(order)}
                        >
                          Print Pallet Labels
                        </button>
                        {order.status === 'PACKAGING' && (
                          <button
                            className="mill-btn mill-btn-secondary"
                            onClick={() => handleMarkReadyToShip(order.id)}
                            disabled={processing === order.id}
                          >
                            {processing === order.id ? '...' : 'Mark Ready'}
                          </button>
                        )}
                        <button
                          className="mill-btn mill-btn-success"
                          onClick={() => handleOpenShipModal(order)}
                          disabled={processing === order.id}
                        >
                          Mark Shipped
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Ship Modal */}
      {showShipModal && selectedOrder && (
        <div className="mill-modal-overlay" onClick={() => setShowShipModal(false)}>
          <div className="mill-modal" onClick={e => e.stopPropagation()}>
            <div className="mill-modal-header">
              <h3>Ship Order #{selectedOrder.order_number}</h3>
              <button className="modal-close" onClick={() => setShowShipModal(false)}>×</button>
            </div>
            <div className="mill-modal-body">
              <div className="ship-summary">
                <p><strong>Customer:</strong> {selectedOrder.customer_name}</p>
                <p><strong>Delivery:</strong> {selectedOrder.delivery_method_display}</p>
                <p><strong>Total Weight:</strong> {selectedOrder.total_weight.toLocaleString()} lbs</p>
              </div>

              {loadingBOLData && (
                <div style={{ padding: '0.5rem', background: '#e3f2fd', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.9rem', color: '#1565c0' }}>
                  Loading BOL data...
                </div>
              )}
              {bolDataLoaded && (
                <div style={{ padding: '0.5rem', background: '#e8f5e9', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.9rem', color: '#2e7d32' }}>
                  ✓ Auto-filled from BOL data
                </div>
              )}

              <div className="form-group">
                <label>Carrier *</label>
                <select
                  className="form-select"
                  value={carrier}
                  onChange={e => setCarrier(e.target.value)}
                >
                  <option value="">Select carrier...</option>
                  <option value="Fastway Freight">Fastway Freight</option>
                  <option value="Peninsula Truck Lines">Peninsula Truck Lines</option>
                  <option value="Customer Pickup">Customer Pickup / Will Call</option>
                  <option value="Self Delivery">Self Delivery (Red Bridge delivers)</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>{carrier === 'Customer Pickup' ? 'Pickup Date' : 'Ship Date'}</label>
                <input
                  type="date"
                  className="form-input"
                  value={shipDate}
                  onChange={e => setShipDate(e.target.value)}
                />
              </div>

              {/* PRO Number field - hidden for Will Call/Self Delivery */}
              {carrier !== 'Customer Pickup' && carrier !== 'Self Delivery' && (
                <div className="form-group">
                  <label>PRO / BOL Number</label>
                  <input
                    type="text"
                    className="form-input"
                    value={proNumber}
                    onChange={e => setProNumber(e.target.value)}
                    placeholder="e.g., 2070878"
                  />
                  <small style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                    {carrier === 'Fastway Freight'
                      ? 'This is the Fastway BOL number - enables Peninsula tracking.'
                      : 'Enter tracking/PRO number for freight shipments.'}
                  </small>
                </div>
              )}

              {/* Tracking number for Other carriers */}
              {carrier === 'Other' && (
                <div className="form-group">
                  <label>Tracking Number</label>
                  <input
                    type="text"
                    className="form-input"
                    value={trackingNumber}
                    onChange={e => setTrackingNumber(e.target.value)}
                    placeholder="Enter tracking number"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  className="form-textarea"
                  value={shippingNotes}
                  onChange={e => setShippingNotes(e.target.value)}
                  placeholder="Any shipping notes..."
                  rows={3}
                />
              </div>
            </div>
            <div className="mill-modal-footer">
              <button
                className="mill-btn mill-btn-secondary"
                onClick={() => setShowShipModal(false)}
              >
                Cancel
              </button>
              <button
                className="mill-btn mill-btn-success"
                onClick={handleMarkShipped}
                disabled={processing === selectedOrder.id || !carrier}
              >
                {processing === selectedOrder.id
                  ? 'Processing...'
                  : carrier === 'Customer Pickup'
                    ? 'Mark Ready for Pickup'
                    : 'Confirm Shipped'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate BOL Modal */}
      {showBOLModal && bolOrder && (
        <div className="mill-modal-overlay" onClick={() => setShowBOLModal(false)}>
          <div className="mill-modal mill-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="mill-modal-header">
              <h3>Generate Bill of Lading</h3>
              <button className="modal-close" onClick={() => setShowBOLModal(false)}>×</button>
            </div>
            <div className="mill-modal-body">
              <div className="ship-summary" style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>Order #{bolOrder.order_number}</h4>
                <p><strong>Customer:</strong> {bolOrder.customer_name}</p>
                <p><strong>Delivery Address:</strong> {bolOrder.delivery_address || 'N/A'}</p>
                <p><strong>Total Weight:</strong> {bolOrder.total_weight.toLocaleString()} lbs</p>
                <p><strong>Items:</strong> {bolOrder.items.length} line item(s)</p>
              </div>

              {/* Existing BOL — pull the ONE record instead of blank-
                  creating (the office may have made it at invoice time).
                  Regenerate refreshes it from the current pallets. */}
              {existingOrderBol && (
                <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#eef6ee', border: '1px solid #cde3cd', borderRadius: '8px' }}>
                  <p style={{ margin: '0 0 0.25rem 0', fontWeight: 700, color: '#1e501e' }}>
                    BOL {existingOrderBol.bol_number} already exists
                    {existingOrderBol.is_stale && (
                      <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 10, background: '#fff3cd', color: '#856404', fontSize: '0.75rem' }}>
                        STALE — pallets changed, regenerate
                      </span>
                    )}
                  </p>
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', color: '#4a5a4a' }}>
                    {existingOrderBol.status_display || existingOrderBol.status}
                    {existingOrderBol.created_by ? ` · created by ${existingOrderBol.created_by}` : ''}
                    {existingOrderBol.created_at ? ` on ${new Date(existingOrderBol.created_at).toLocaleString()}` : ''}
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button className="mill-btn mill-btn-primary mill-btn-sm" onClick={handlePrintExistingBOL} disabled={bolActionWorking}>
                      {bolActionWorking ? 'Working...' : 'Print This BOL'}
                    </button>
                    <button className="mill-btn mill-btn-secondary mill-btn-sm" onClick={handleRegenerateExistingBOL} disabled={bolActionWorking}>
                      Regenerate from Pallets
                    </button>
                  </div>
                  <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.8rem', color: '#666' }}>
                    Only fill the form below if you need to REPLACE this BOL with a new number.
                  </p>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>Tracking/BOL # * <span style={{ fontWeight: 'normal', fontSize: '0.85rem', color: '#666' }}>(from shipper barcode)</span></label>
                  <input
                    type="text"
                    className="form-input"
                    value={bolTrackingNumber}
                    onChange={e => setBolTrackingNumber(e.target.value)}
                    placeholder="Enter tracking/BOL number"
                    required
                    style={{ borderColor: !bolTrackingNumber ? '#dc3545' : undefined }}
                  />
                </div>

                <div className="form-group">
                  <label>Quote # <span style={{ fontWeight: 'normal', fontSize: '0.85rem', color: '#666' }}>(optional)</span></label>
                  <input
                    type="text"
                    className="form-input"
                    value={bolQuoteNumber}
                    onChange={e => setBolQuoteNumber(e.target.value)}
                    placeholder="Enter quote number"
                  />
                </div>

                <div className="form-group">
                  <label>Ship Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={bolShipDate}
                    onChange={e => setBolShipDate(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Carrier *</label>
                  <select
                    className="form-select"
                    value={bolCarrier}
                    onChange={e => setBolCarrier(e.target.value)}
                    required
                  >
                    <option value="FASTWAY">Fastway Freight (LTL)</option>
                    <option value="PENINSULA">Peninsula Truck Lines</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Driver Name (optional)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={bolDriverName}
                    onChange={e => setBolDriverName(e.target.value)}
                    placeholder="Enter driver name"
                  />
                </div>

                <div className="form-group">
                  <label>Trailer Number (optional)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={bolTrailerNumber}
                    onChange={e => setBolTrailerNumber(e.target.value)}
                    placeholder="Enter trailer number"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Special Instructions (optional)</label>
                <textarea
                  className="form-textarea"
                  value={bolSpecialInstructions}
                  onChange={e => setBolSpecialInstructions(e.target.value)}
                  placeholder="Any special handling or delivery instructions..."
                  rows={3}
                />
              </div>

              <div style={{
                background: '#f5f5f5',
                padding: '1rem',
                borderRadius: '8px',
                marginTop: '1rem'
              }}>
                <h5 style={{ margin: '0 0 0.5rem 0' }}>Shipper Information</h5>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                  Red Bridge Farm Feed Co.<br />
                  145 W 3rd Ave<br />
                  Kettle Falls, WA 99141<br />
                  509-738-9004
                </p>
              </div>
            </div>
            <div className="mill-modal-footer">
              <button
                className="mill-btn mill-btn-secondary"
                onClick={() => setShowBOLModal(false)}
                disabled={generatingBOL}
              >
                Cancel
              </button>
              <button
                className="mill-btn mill-btn-primary"
                onClick={() => handleGenerateBOL(false)}
                disabled={generatingBOL || !bolShipDate || !bolTrackingNumber}
              >
                {generatingBOL ? 'Generating...' : 'Generate & Download BOL'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pallet Label Modal */}
      {showPalletModal && palletOrder && (
        <div className="mill-modal-overlay" onClick={() => setShowPalletModal(false)}>
          <div className="mill-modal mill-modal-xl" onClick={e => e.stopPropagation()}>
            <div className="mill-modal-header">
              <h3>Print Pallet Labels - Order #{palletOrder.order_number}</h3>
              <button className="modal-close" onClick={() => setShowPalletModal(false)}>×</button>
            </div>
            <div className="mill-modal-body">
              {/* Order Summary */}
              <div className="ship-summary" style={{ marginBottom: '1.5rem' }}>
                <p><strong>Customer:</strong> {palletOrder.customer_name}</p>
                <p><strong>Total Weight:</strong> {palletOrder.total_weight.toLocaleString()} lbs</p>
                <p><strong>Items:</strong></p>
                <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                  {palletOrder.items.map(item => (
                    <li key={item.id}>
                      {item.formula_name || 'Unnamed item'} - {item.number_of_packages} {item.package_type_display} ({item.quantity_lbs.toLocaleString()} lbs)
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pallet Configuration */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', marginBottom: '1.5rem', alignItems: 'end' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Ship Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={palletShipDate}
                    onChange={e => setPalletShipDate(e.target.value)}
                  />
                </div>
                <button
                  className="mill-btn mill-btn-secondary"
                  onClick={handleRefreshPallets}
                  disabled={loadingPallets}
                  style={{ height: '42px' }}
                >
                  {loadingPallets ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {/* Pallet Breakdown */}
              {loadingPallets ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div className="mill-loading-spinner"></div>
                  <p>Loading packed pallets...</p>
                </div>
              ) : (
                <div className="pallet-breakdown">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0 }}>Packed Pallets ({pallets.length} pallet{pallets.length !== 1 ? 's' : ''})</h4>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>
                      Labels print from the packed pallets — always matching the BOL
                    </span>
                  </div>

                  {pallets.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '2rem', background: '#f5f5f5', borderRadius: '8px' }}>
                      <p>
                        {palletError ||
                          'No packed pallets for this order yet — pack it in Packaging first. Labels print from the packed pallets so they always match the BOL.'}
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      {pallets.map((pallet, palletIndex) => (
                        <div
                          key={palletIndex}
                          style={{
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            padding: '1rem',
                            background: '#fafafa',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <h5 style={{ margin: 0, color: '#1e501e' }}>
                              Pallet {palletIndex + 1} of {pallets.length}
                            </h5>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.9rem' }}>
                                <strong>{pallet.total_bags}</strong> bags | <strong>{pallet.total_weight.toLocaleString()}</strong> lbs
                              </span>
                            </div>
                          </div>

                          {pallet.contents.length === 0 ? (
                            <p style={{ margin: 0, color: '#666', fontStyle: 'italic' }}>Empty pallet</p>
                          ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid #ddd' }}>
                                  <th style={{ textAlign: 'left', padding: '0.5rem' }}>Product</th>
                                  <th style={{ textAlign: 'center', padding: '0.5rem', width: '100px' }}>Quantity</th>
                                  <th style={{ textAlign: 'right', padding: '0.5rem', width: '100px' }}>Weight</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pallet.contents.map((item, itemIndex) => (
                                  <tr key={itemIndex} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '0.5rem' }}>
                                      {item.feed_name}
                                      {(item.lot_numbers?.length ?? 0) > 0 && (
                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                          Lot {item.lot_numbers!.join(', ')}
                                        </div>
                                      )}
                                    </td>
                                    <td style={{ padding: '0.5rem', textAlign: 'center' }}>{item.quantity} bags</td>
                                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{item.weight.toLocaleString()} lbs</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}

                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Label Preview */}
              {showPreview && labelPreviews.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ marginBottom: '1rem' }}>Label Preview</h4>
                  <div style={{ display: 'flex', gap: '1rem', overflowX: 'auto', padding: '0.5rem' }}>
                    {labelPreviews.map((preview, idx) => (
                      <div
                        key={idx}
                        style={{
                          flexShrink: 0,
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}
                      >
                        <img
                          src={`data:image/png;base64,${preview}`}
                          alt={`Pallet ${idx + 1} label`}
                          style={{ width: '400px', height: 'auto' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mill-modal-footer">
              <button
                className="mill-btn mill-btn-secondary"
                onClick={() => setShowPalletModal(false)}
                disabled={generatingLabels}
              >
                Cancel
              </button>
              <button
                className="mill-btn mill-btn-outline"
                onClick={handlePreviewLabels}
                disabled={generatingLabels || pallets.length === 0 || loadingPallets}
              >
                {generatingLabels && !showPreview ? 'Generating...' : 'Preview Labels'}
              </button>
              <button
                className="mill-btn mill-btn-primary"
                onClick={handlePrintPalletLabels}
                disabled={generatingLabels || pallets.length === 0 || loadingPallets}
              >
                {generatingLabels && showPreview ? 'Printing...' : `Print ${pallets.length} Label${pallets.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace BOL Confirmation Modal */}
      {showReplaceModal && existingBOL && (
        <div className="mill-modal-overlay" onClick={() => setShowReplaceModal(false)}>
          <div className="mill-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="mill-modal-header">
              <h3>BOL Already Exists</h3>
              <button className="modal-close" onClick={() => setShowReplaceModal(false)}>×</button>
            </div>
            <div className="mill-modal-body">
              <div style={{
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
              }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: '600', color: '#856404' }}>
                  A BOL already exists for this order:
                </p>
                <p style={{ margin: 0, color: '#856404' }}>
                  <strong>BOL #:</strong> {existingBOL.bol_number}<br />
                  <strong>Status:</strong> {existingBOL.status_display}
                </p>
              </div>
              <p style={{ margin: 0, color: '#666' }}>
                Do you want to replace it with a new BOL? The existing BOL will be voided.
              </p>
            </div>
            <div className="mill-modal-footer">
              <button
                className="mill-btn mill-btn-secondary"
                onClick={() => {
                  setShowReplaceModal(false);
                  setExistingBOL(null);
                }}
              >
                Cancel
              </button>
              <button
                className="mill-btn mill-btn-success"
                onClick={() => handleGenerateBOL(true)}
                disabled={generatingBOL}
              >
                {generatingBOL ? 'Replacing...' : 'Replace BOL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MillShipping;
